/**
 * Variant Selection Dialog Component
 * Custom component for variant selection functionality
 * with dynamic price updates and image fallback
 *
 * Note: This component implements dialog functionality directly
 * to avoid ES6 module dependencies
 */

/**
 * Rewrites a Shopify CDN image URL to request a specific width
 * @param {string} url - The original image URL
 * @param {number} targetWidth - The desired width
 * @returns {string} - The modified URL
 */
function resizeShopifyImageUrl(url, targetWidth) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cdn.shopify.com')) return url;

  try {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('width', targetWidth.toString());
    urlObj.searchParams.delete('height');
    return urlObj.href;
  } catch {
    if (url.includes('width=')) {
      return url.replace(/width=\d+/, `width=${targetWidth}`);
    }
    return url + (url.includes('?') ? '&' : '?') + `width=${targetWidth}`;
  }
}

class VariantSelectionDialog extends HTMLElement {
  requiredRefs = ['dialog'];

  constructor() {
    super();
    this.currentProduct = null;
    this.selectedOptions = new Map();
    this.selectedVariant = null;
    this.refs = {};
    this.scrollY = 0;
  }

  connectedCallback() {
    // Initialize refs system
    this.updateRefs();
    
    // Set up close button event listener
    const closeButton = this.querySelector('[data-close-dialog]');
    if (closeButton) {
      closeButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.closeDialog();
      });
    }

    // Set up add to cart button event listener
    const addToCartBtn = this.querySelector('[data-variant-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
    }

    // Set up compare button event listener
    const compareBtn = this.querySelector('[data-bundle-compare-btn]');
    if (compareBtn) {
      compareBtn.addEventListener('click', this.toggleCompareTable.bind(this));
    }
  }

  /**
   * Update refs object with elements that have ref attributes
   */
  updateRefs() {
    const refs = {};
    const elements = this.querySelectorAll('[ref]');
    
    for (const element of elements) {
      const refName = element.getAttribute('ref');
      if (refName) {
        refs[refName] = element;
      }
    }
    
    // Check required refs
    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          throw new Error(`Required ref "${ref}" not found in component ${this.tagName.toLowerCase()}`);
        }
      }
    }
    
    this.refs = refs;
  }

  /**
   * Show the dialog
   */
  showDialog() {
    const dialog = this.refs.dialog;
    if (!dialog || dialog.open) return;

    // Store scroll position for restoration later
    this.scrollY = window.scrollY;
    
    // Prevent body scrolling
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    dialog.showModal();
    this.dispatchEvent(new CustomEvent('dialog:open'));

    // Wait until the next tick to add the event listeners
    setTimeout(() => {
      this.addEventListener('click', this.handleDialogClick.bind(this));
      this.addEventListener('keydown', this.handleKeyDown.bind(this));
    });
  }

  /**
   * Close the dialog
   */
  closeDialog = async () => {
    const dialog = this.refs.dialog;
    if (!dialog || !dialog.open) return;

    this.removeEventListener('click', this.handleDialogClick);
    this.removeEventListener('keydown', this.handleKeyDown);

    dialog.classList.add('dialog-closing');

    // Wait for animation to complete
    await new Promise(resolve => {
      const handleAnimationEnd = () => {
        dialog.removeEventListener('animationend', handleAnimationEnd);
        resolve();
      };
      dialog.addEventListener('animationend', handleAnimationEnd);
      
      // Fallback timeout in case animation doesn't fire
      setTimeout(resolve, 300);
    });

    // Restore body scrolling
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    
    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new CustomEvent('dialog:close'));
  };

  /**
   * Handle clicks on the dialog (close when clicking outside)
   * @param {MouseEvent} event
   */
  handleDialogClick(event) {
    const dialog = this.refs.dialog;
    const rect = dialog.getBoundingClientRect();
    const isOutside = event.clientX < rect.left || 
                     event.clientX > rect.right || 
                     event.clientY < rect.top || 
                     event.clientY > rect.bottom;
    
    if (isOutside) {
      this.closeDialog();
    }
  }

  /**
   * Handle keydown events (close on Escape)
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    }
  }

  /**
   * Open the variant selection dialog for a specific product
   * @param {Object} product - The product data from the studiotools-main-scroll
   */
  openVariantSelection(product) {
    if (!product || !product.hasVariants || !product.options?.length) {
      console.warn('Product does not have variants or options');
      return;
    }


    this.currentProduct = product;
    this.selectedOptions.clear();
    this.selectedVariant = null;

    // Populate dialog content
    this.populateContent(product);
    
    // Show the dialog
    this.showDialog();
    
    // Initialize variant selection based on current product variant
    this.initializeSelection();
  }

  /**
   * Populate the dialog content with product data
   * @param {Object} product - The product data
   */
  populateContent(product) {
    // Set title
    const titleElement = this.querySelector('[data-variant-title]');
    if (titleElement) {
      titleElement.textContent = product.title;
    }

    // Set initial image with fallback logic
    this.updateProductImage();

    // Set initial price
    this.updatePrice();

    // Create variant options
    this.createVariantOptions();

    // Set initial availability
    this.updateAvailability();
  }

  /**
   * Update product image with fallback to main product image
   */
  updateProductImage() {
    const imageElement = this.querySelector('[data-variant-image]');
    if (!imageElement || !this.currentProduct) return;

    let imageUrl = '';
    let altText = this.currentProduct.title;

    // Try to get variant-specific image first
    // The featured_image is now included directly in the variant data
    if (this.selectedVariant && this.selectedVariant.featured_image) {
      imageUrl = this.selectedVariant.featured_image;
      // Add the variant title to the alt text for better accessibility
      altText = `${this.currentProduct.title} - ${this.selectedVariant.title}`;
    } 
    // Fallback to main product image from media array
    else if (this.currentProduct.media && this.currentProduct.media.length > 0) {
      const firstMedia = this.currentProduct.media[0];
      if (firstMedia.type === 'image') {
        // Use the media URL, prioritizing the processed URL over the raw mediaFile
        imageUrl = firstMedia.url || firstMedia.mediaFile;
        altText = firstMedia.alt || this.currentProduct.title;
      }
    }

    if (imageUrl) {
      // The URL from Shopify already includes proper sizing parameters
      // Only add sizing if it's a raw URL without parameters
      if (!imageUrl.includes('?') && !imageUrl.includes('_') && !imageUrl.includes('//cdn.shopify.com')) {
        imageUrl = imageUrl + '?width=800';
      }
      
      imageElement.src = imageUrl;
      imageElement.alt = altText;
      imageElement.style.display = 'block';
    } else {
      imageElement.style.display = 'none';
    }
  }

  /**
   * Check if prices should be shown based on access control
   * @returns {boolean} Whether prices should be displayed
   */
  shouldShowPrices() {
    if (typeof ACCESS_CONTROL === 'undefined') return true;
    if (ACCESS_CONTROL.mode === 'full_public') return true;
    if (ACCESS_CONTROL.mode === 'limited_public' && ACCESS_CONTROL.isLoggedIn) return true;
    return false;
  }

  /**
   * Update price display
   */
  updatePrice() {
    const priceElement = this.querySelector('[data-variant-price]');
    if (!priceElement) return;

    // Check access control
    if (!this.shouldShowPrices()) {
      priceElement.innerHTML = `<a href="${ACCESS_CONTROL.loginUrl}" class="price-login-link">${ACCESS_CONTROL.loginMessage}</a>`;
      return;
    }

    let price = this.currentProduct.price;
    let unitPriceHtml = '';

    if (this.selectedVariant) {
      price = this.selectedVariant.price;

      // Check for unit price
      if (this.selectedVariant.unit_price) {
        const formattedUnitPrice = this.formatPrice(this.selectedVariant.unit_price);
        const measurement = this.selectedVariant.unit_price_measurement;
        let unitLabel = '';

        if (measurement && measurement.reference_unit) {
          unitLabel = `/${measurement.reference_value > 1 ? measurement.reference_value : ''}${measurement.reference_unit}`;
        }

        unitPriceHtml = `<span class="variant-selection__unit-price">${formattedUnitPrice}${unitLabel}</span>`;
      }
    }

    priceElement.innerHTML = `<span class="variant-selection__main-price">${price}</span>${unitPriceHtml}`;

    // Update preorder button visibility
    this.updatePreorderButton();
  }

  /**
   * Update preorder button display based on selected variant
   */
  updatePreorderButton() {
    const buttonContainer = this.querySelector('[data-variant-preorder-button]');
    if (!buttonContainer) {
      console.log('Preorder: Button container not found');
      return;
    }

    // Check if the selected variant has preorder metafield
    const isPreorder = this.selectedVariant?.metafields?.custom?.is_pre_order_only;

    console.log('Preorder check:', {
      selectedVariant: this.selectedVariant,
      metafields: this.selectedVariant?.metafields,
      custom: this.selectedVariant?.metafields?.custom,
      is_pre_order_only: isPreorder
    });

    if (isPreorder) {
      console.log('Showing preorder button');
      // Create and show preorder button
      buttonContainer.innerHTML = `
        <a
          href="/pages/discounts-pricing"
          class="preorder-button page-popup-trigger"
          data-page-handle="discounts-pricing"
          data-popup-initialized="true"
          aria-label="Preorder information"
        >
          <span class="preorder-button__icon">⚠️</span>
          <span class="preorder-button__text">PREORDER</span>
        </a>
      `;

      // Re-initialize page popup triggers for the new button
      if (window.pagePopupTriggerInstance) {
        const variantDialog = this.querySelector('.variant-selection__dialog');
        if (variantDialog) {
          window.pagePopupTriggerInstance.setupNestedTriggers(variantDialog);
        }
      }
    } else {
      console.log('Hiding preorder button (isPreorder =', isPreorder, ')');
      // Hide preorder button
      buttonContainer.innerHTML = '';
    }
  }

  /**
   * Update availability display
   */
  updateAvailability() {
    const availabilityElement = this.querySelector('[data-variant-availability]');
    if (!availabilityElement) return;

    let isAvailable = false;
    let availabilityText = 'Please select options';

    if (this.selectedVariant) {
      isAvailable = this.selectedVariant.available;
      availabilityText = isAvailable ? 'In stock' : 'Out of stock';
    } else if (this.selectedOptions.size === this.currentProduct.options.length) {
      availabilityText = 'Variant not available';
    }

    availabilityElement.textContent = availabilityText;
    availabilityElement.className = `variant-selection__availability ${isAvailable ? 'available' : 'unavailable'}`;

    // Update add to cart button state
    const addToCartBtn = this.querySelector('[data-variant-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.disabled = !this.selectedVariant || !isAvailable;
    }
  }

  /**
   * Create variant option controls
   */
  createVariantOptions() {
    const optionsContainer = this.querySelector('[data-variant-options]');
    if (!optionsContainer || !this.currentProduct.options) return;

    optionsContainer.innerHTML = '';

    this.currentProduct.options.forEach((option) => {
      const optionElement = this.createOptionElement(option);
      optionsContainer.appendChild(optionElement);
    });
  }

  /**
   * Create a single option element
   * @param {Object} option - The option data
   * @returns {HTMLElement} The option element
   */
  createOptionElement(option) {
    const optionDiv = document.createElement('div');
    optionDiv.className = 'variant-option';
    optionDiv.setAttribute('data-option-name', option.name);

    const labelElement = document.createElement('label');
    labelElement.className = 'variant-option__label';
    labelElement.textContent = option.name;
    labelElement.setAttribute('for', `option-${option.name.toLowerCase().replace(/\s+/g, '-')}`);

    const selectElement = document.createElement('select');
    selectElement.className = 'variant-option__select';
    selectElement.id = `option-${option.name.toLowerCase().replace(/\s+/g, '-')}`;
    selectElement.setAttribute('data-option-name', option.name);
    selectElement.setAttribute('aria-label', `Select ${option.name}`);

    // Add default option
    const defaultOption = document.createElement('option');
    defaultOption.value = '';
    defaultOption.textContent = `Choose ${option.name}`;
    defaultOption.disabled = true;
    defaultOption.selected = true;
    selectElement.appendChild(defaultOption);

    option.values.forEach((valueData) => {
      const optionElement = document.createElement('option');
      optionElement.value = valueData.value;
      optionElement.textContent = valueData.value;
      optionElement.setAttribute('data-option-value', valueData.value);

      // Check if this value is available in any variant
      const isValueAvailable = this.isOptionValueAvailable(option.name, valueData.value);
      
      if (!isValueAvailable) {
        optionElement.disabled = true;
        optionElement.textContent += ' (Unavailable)';
      }

      selectElement.appendChild(optionElement);
    });

    // Add change event listener
    selectElement.addEventListener('change', (event) => {
      const selectedValue = event.target.value;
      if (selectedValue) {
        this.selectOptionValue(option.name, selectedValue);
      }
    });

    optionDiv.appendChild(labelElement);
    optionDiv.appendChild(selectElement);

    return optionDiv;
  }

  /**
   * Check if an option value is available in any variant
   * @param {string} optionName - The option name
   * @param {string} optionValue - The option value
   * @returns {boolean} Whether the value is available
   */
  isOptionValueAvailable(optionName, optionValue) {
    if (!this.currentProduct || !this.currentProduct.variants) {
      console.error('No product or variants data available');
      return false;
    }
    
    // Check if any available variant has this option value
    const availableVariants = this.currentProduct.variants.filter(variant => {
      // CRITICAL FIX: Let's be more permissive about availability
      // Sometimes the available flag might not be set correctly
      const isVariantAvailable = variant.available !== false && variant.available !== 'false';
      
      if (!isVariantAvailable && variant.available !== undefined) {
        return false;
      }
      
      // Approach 1: Try selectedOptions array first
      if (variant.selectedOptions && Array.isArray(variant.selectedOptions)) {
        const hasOptionValue = variant.selectedOptions.some(opt => {
          // Normalize strings to handle potential whitespace/case issues
          const normalizedOptName = String(opt.name).trim();
          const normalizedOptValue = String(opt.value).trim();
          const normalizedTargetName = String(optionName).trim();
          const normalizedTargetValue = String(optionValue).trim();
          
          return normalizedOptName === normalizedTargetName && normalizedOptValue === normalizedTargetValue;
        });
        
        if (hasOptionValue) {
          return true;
        }
      }
      
      // Approach 2: Fallback to option1, option2, option3 properties
      const optionPosition = this.currentProduct.options.findIndex(opt => opt.name === optionName);
      
      if (optionPosition >= 0) {
        const optionKey = `option${optionPosition + 1}`;
        const variantOptionValue = variant[optionKey];
        
        // Normalize for comparison
        const normalizedVariantValue = String(variantOptionValue || '').trim();
        const normalizedTargetValue = String(optionValue).trim();
        
        const matches = normalizedVariantValue === normalizedTargetValue;
        if (matches) {
          return true;
        }
      }
      
      return false;
    });

    return availableVariants.length > 0;
  }

  /**
   * Select an option value
   * @param {string} optionName - The option name
   * @param {string} optionValue - The option value
   */
  selectOptionValue(optionName, optionValue) {
    // Update selected options
    this.selectedOptions.set(optionName, optionValue);

    // Update UI to reflect selection
    this.updateOptionUI(optionName, optionValue);

    // Find matching variant
    this.findMatchingVariant();

    // Update price, availability, image, and bundle display
    this.updatePrice();
    this.updateAvailability();
    this.updateProductImage();
    this.updateBundleDisplay();
  }

  /**
   * Update the UI for option selection
   * @param {string} optionName - The option name
   * @param {string} selectedValue - The selected value
   */
  updateOptionUI(optionName, selectedValue) {
    const selectElement = this.querySelector(`select[data-option-name="${optionName}"]`);
    if (!selectElement) return;

    selectElement.value = selectedValue;
  }

  /**
   * Find variant that matches selected options
   */
  findMatchingVariant() {
    if (this.selectedOptions.size !== this.currentProduct.options.length) {
      this.selectedVariant = null;
      console.log('Not all options selected, clearing variant');
      return;
    }

    const matchingVariant = this.currentProduct.variants.find(variant => {
      // Approach 1: Try selectedOptions array first - BUT ONLY if it's not empty
      if (variant.selectedOptions && Array.isArray(variant.selectedOptions) && variant.selectedOptions.length > 0) {
        // CRITICAL FIX: Ensure selectedOptions array has the expected number of options
        if (variant.selectedOptions.length !== this.currentProduct.options.length) {
          // Fall through to option1/option2/option3 approach
        } else {
          const matches = variant.selectedOptions.every(option => {
            const selectedValue = this.selectedOptions.get(option.name);
            return selectedValue === option.value;
          });
          return matches;
        }
      }
      
      // Approach 2: Fallback to option1, option2, option3 properties
      const matches = this.currentProduct.options.every((option, idx) => {
        const optionKey = `option${idx + 1}`;
        const variantValue = variant[optionKey];
        const selectedValue = this.selectedOptions.get(option.name);
        return selectedValue === variantValue;
      });
      return matches;
    });

    this.selectedVariant = matchingVariant || null;
    
    if (this.selectedVariant) {
      console.log('Found matching variant:', this.selectedVariant.id, 'with image:', this.selectedVariant.featured_image);
    } else {
      console.log('No matching variant found for selected options');
    }
  }

  /**
   * Initialize selection - always preselect first available option for each category
   */
  initializeSelection() {
    if (!this.currentProduct || !this.currentProduct.variants || !this.currentProduct.options) return;

    // Reset all dropdowns to default state
    const selectElements = this.querySelectorAll('.variant-option__select');
    selectElements.forEach(select => {
      select.selectedIndex = 0; // Reset to the first option (disabled default)
    });

    // Always initialize with first available options for each category
    this.currentProduct.options.forEach(option => {
      // Find the first option value that has at least one available variant
      const availableValue = option.values.find(valueData => {
        return this.isOptionValueAvailable(option.name, valueData.value);
      });
      
      if (availableValue) {
        this.selectedOptions.set(option.name, availableValue.value);
        this.updateOptionUI(option.name, availableValue.value);
      } else {
        console.warn(`No available options found for ${option.name}`);
      }
    });

    // Find the matching variant based on selected options
    this.findMatchingVariant();

    // Update UI elements
    this.updatePrice();
    this.updateAvailability();
    this.updateProductImage();
    this.updateBundleDisplay();
  }

  /**
   * Update bundle display based on selected variant
   */
  updateBundleDisplay() {
    const bundleSection = this.querySelector('[data-variant-bundle-section]');
    const bundleGrid = this.querySelector('[data-variant-bundle-grid]');

    if (!bundleSection || !bundleGrid) return;

    // Check if current variant is a bundle
    const isBundle = this.isCurrentVariantBundle();

    if (isBundle) {
      bundleSection.style.display = 'block';
      this.populateBundleGrid();
      this.updateCompareUI();
    } else {
      bundleSection.style.display = 'none';
    }
  }

  /**
   * Check if the current selected variant is a bundle
   * @returns {boolean} Whether the current variant is a bundle
   */
  isCurrentVariantBundle() {
    if (!this.selectedVariant) return false;

    // Check if the variant has bundle metafields
    const bundledVariants = this.selectedVariant.metafields?.simple_bundles?.bundled_variants?.value;
    return bundledVariants && bundledVariants.length > 0;
  }

  /**
   * Populate bundle grid with bundled products
   */
  async populateBundleGrid() {
    const bundleGrid = this.querySelector('[data-variant-bundle-grid]');
    if (!bundleGrid || !this.selectedVariant) return;

    const bundledVariants = this.selectedVariant.metafields?.simple_bundles?.bundled_variants?.value;
    if (!bundledVariants || bundledVariants.length === 0) return;

    // Clear existing content
    bundleGrid.innerHTML = '';

    // Create bundle items
    for (const bundledVariant of bundledVariants) {
      const bundleItem = await this.createBundleItem(bundledVariant);
      if (bundleItem) {
        bundleGrid.appendChild(bundleItem);
      }
    }

    // Initialize page popup system for new bundle triggers
    if (window.pagePopupTriggerInstance) {
      // Use setupNestedTriggers method which handles the proper initialization
      // Pass the variant dialog as the container
      const variantDialog = this.querySelector('.variant-selection__dialog');
      if (variantDialog) {
        window.pagePopupTriggerInstance.setupNestedTriggers(variantDialog);
      }
    }
  }

  /**
   * Create a bundle item element
   * @param {Object} bundledVariant - The bundled variant data
   * @returns {HTMLElement|null} The bundle item element
   */
  async createBundleItem(bundledVariant) {
    // Try to get product data
    const product = await this.fetchProductData(bundledVariant);
    if (!product) return null;

    // Get product handle for link
    const productHandle = bundledVariant.handle || bundledVariant.product_handle;
    if (!productHandle) return null;

    const item = document.createElement('a');
    item.className = 'variant-selection__bundle-item bundle-popup-trigger';
    item.href = `/products/${productHandle}?view=product-info`;
    item.setAttribute('data-product-handle', productHandle);
    item.setAttribute('aria-label', `View ${product.title}`);

    // Create image
    const image = document.createElement('img');
    image.className = 'variant-selection__bundle-image';
    image.loading = 'lazy';

    if (product.featured_image) {
      image.src = resizeShopifyImageUrl(product.featured_image, 400);
      image.alt = product.title;
    } else {
      // Fallback placeholder
      image.style.background = '#f5f5f5';
      image.alt = product.title;
    }

    // Create info container
    const info = document.createElement('div');
    info.className = 'variant-selection__bundle-info';

    // Create name with variant details
    const name = document.createElement('div');
    name.className = 'variant-selection__bundle-name';
    const quantity = bundledVariant.quantity_in_bundle > 0 ? `${bundledVariant.quantity_in_bundle}x ` : '';

    // Try to get specific variant title if available
    let productTitle = product.title;
    if (bundledVariant.variant_id && product.variants) {
      const specificVariant = product.variants.find(v => v.id == bundledVariant.variant_id);
      if (specificVariant && specificVariant.title && specificVariant.title !== 'Default Title') {
        productTitle = `${product.title} - ${specificVariant.title}`;
      }
    }

    name.textContent = `${quantity}${productTitle}`;

    // Create price
    const price = document.createElement('div');
    price.className = 'variant-selection__bundle-price';

    // Check access control for bundle prices
    if (!this.shouldShowPrices()) {
      price.innerHTML = `<a href="${ACCESS_CONTROL.loginUrl}" class="price-login-link">${ACCESS_CONTROL.loginMessage}</a>`;
    } else {
      // Try to get specific variant price if available
      let variantPrice = product.price;
      if (bundledVariant.variant_id && product.variants) {
        const specificVariant = product.variants.find(v => v.id == bundledVariant.variant_id);
        if (specificVariant) {
          variantPrice = specificVariant.price;
        }
      }

      price.textContent = variantPrice;
    }

    info.appendChild(name);
    info.appendChild(price);

    item.appendChild(image);
    item.appendChild(info);

    return item;
  }

  /**
   * Fetch product data for bundled product
   * @param {Object} bundledVariant - The bundled variant data
   * @returns {Object|null} The product data
   */
  async fetchProductData(bundledVariant) {
    try {
      // The bundle data uses 'handle' property, not 'product_handle'
      let productHandle = bundledVariant.handle || bundledVariant.product_handle;

      // If no handle, try to fetch from product_id using collections endpoint
      if (!productHandle && bundledVariant.product_id) {
        try {
          // Try to fetch the product by ID from the all collection
          const response = await fetch(`/collections/all/products.json`);
          if (response.ok) {
            const data = await response.json();
            const product = data.products.find(p => p.id == bundledVariant.product_id);
            if (product) {
              productHandle = product.handle;
            }
          }
        } catch (e) {
          console.warn('Could not fetch product by ID:', e);
        }
      }

      if (productHandle) {
        // Try to fetch from Shopify's product JSON endpoint
        const response = await fetch(`/products/${productHandle}.js`);
        if (response.ok) {
          const productData = await response.json();

          // Transform the data to match our expected format
          return {
            title: productData.title,
            handle: productData.handle,
            url: `/products/${productData.handle}`,
            price: this.formatPrice(productData.price),
            featured_image: productData.featured_image,
            variants: productData.variants?.map(v => ({
              id: v.id,
              price: this.formatPrice(v.price),
              title: v.title
            }))
          };
        }
      }

      // If all fetching fails, return fallback data
      throw new Error('Could not fetch product data');
    } catch (error) {
      console.log('Using fallback data for bundled product:', error.message);

      // Enhanced fallback - create a display-ready product object
      const fallbackTitle = bundledVariant.product_title || bundledVariant.title || 'Bundle Product';
      const fallbackPrice = bundledVariant.price ? this.formatPrice(bundledVariant.price) : 'See product page';
      const productHandle = bundledVariant.handle || bundledVariant.product_handle;

      return {
        title: fallbackTitle,
        handle: productHandle || 'unknown',
        url: productHandle ? `/products/${productHandle}` : '#',
        price: fallbackPrice,
        featured_image: bundledVariant.featured_image || null,
        variants: []
      };
    }
  }

  /**
   * Format price from cents to currency string
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    if (typeof priceInCents !== 'number') return 'Price unavailable';

    // Convert cents to currency (assuming CHF based on the theme)
    const price = priceInCents / 100;
    return `CHF ${price.toFixed(2)}`;
  }

  // =============================================
  // Bundle Comparison Table Methods
  // =============================================

  /**
   * Check if compare button should be shown (bundle has >3 products)
   * @returns {boolean} Whether to show compare button
   */
  shouldShowCompareButton() {
    if (!this.selectedVariant) return false;

    const bundledVariants = this.selectedVariant.metafields?.simple_bundles?.bundled_variants?.value;
    return bundledVariants && bundledVariants.length > 3;
  }

  /**
   * Get all bundle variants for the current product
   * @returns {Array} Array of variants that are bundles
   */
  getAllBundleVariants() {
    if (!this.currentProduct?.variants) return [];

    return this.currentProduct.variants.filter(variant => {
      const bundledVariants = variant.metafields?.simple_bundles?.bundled_variants?.value;
      return bundledVariants && bundledVariants.length > 0;
    });
  }

  /**
   * Build comparison matrix data
   * @returns {Object|null} Matrix data with products and variant quantities
   */
  buildComparisonMatrix() {
    const bundleVariants = this.getAllBundleVariants();
    if (bundleVariants.length === 0) return null;

    // Map to collect all unique products across all bundles
    // Key: product_handle or product_id, Value: product info
    const allProducts = new Map();

    // Process each bundle variant
    bundleVariants.forEach(variant => {
      const bundledProducts = variant.metafields?.simple_bundles?.bundled_variants?.value || [];

      bundledProducts.forEach(product => {
        const key = product.product_handle || product.handle || product.product_id;

        if (!allProducts.has(key)) {
          allProducts.set(key, {
            handle: product.product_handle || product.handle,
            title: product.product_title || product.title,
            image: product.featured_image,
            variantTitle: product.variant_title,
            quantities: new Map()
          });
        }

        // Store quantity for this variant
        const productInfo = allProducts.get(key);
        productInfo.quantities.set(variant.id, product.quantity_in_bundle || 1);
      });
    });

    return {
      bundleVariants,
      products: Array.from(allProducts.values()),
      selectedVariantId: this.selectedVariant?.id
    };
  }

  /**
   * Render comparison table
   */
  renderComparisonTable() {
    const tableContainer = this.querySelector('[data-bundle-compare-table]');
    if (!tableContainer) return;

    const matrix = this.buildComparisonMatrix();
    if (!matrix || matrix.products.length === 0) {
      tableContainer.style.display = 'none';
      return;
    }

    const { bundleVariants, products, selectedVariantId } = matrix;

    // Build table HTML
    let html = `
      <div class="variant-selection__compare-table-wrapper">
        <table class="variant-selection__compare-table">
          <thead>
            <tr>
              <th class="compare-table__product-header">Produkt</th>
              ${bundleVariants.map(v => `
                <th class="compare-table__variant-header${v.id === selectedVariantId ? ' compare-table__variant-header--active' : ''}"
                    data-variant-id="${v.id}">
                  <span class="compare-table__variant-name">${this.getVariantDisplayName(v)}</span>
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${products.map(product => `
              <tr class="compare-table__row">
                <td class="compare-table__product-cell">
                  <div class="compare-table__product">
                    ${product.image ? `<img src="${resizeShopifyImageUrl(product.image, 72)}" alt="${product.title}" class="compare-table__product-image" />` : ''}
                    <span class="compare-table__product-name">${product.title}${product.variantTitle && product.variantTitle !== 'Default Title' ? ` - ${product.variantTitle}` : ''}</span>
                  </div>
                </td>
                ${bundleVariants.map(v => {
                  const qty = product.quantities.get(v.id);
                  const isActive = v.id === selectedVariantId;
                  return `
                    <td class="compare-table__quantity-cell${isActive ? ' compare-table__quantity-cell--active' : ''}${!qty ? ' compare-table__quantity-cell--empty' : ''}">
                      ${qty ? `${qty}x` : '—'}
                    </td>
                  `;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    `;

    tableContainer.innerHTML = html;
  }

  /**
   * Get display name for variant
   * @param {Object} variant - The variant object
   * @returns {string} Display name
   */
  getVariantDisplayName(variant) {
    if (variant.title && variant.title !== 'Default Title') {
      return variant.title;
    }

    // Try to build from selected options
    if (variant.selectedOptions && variant.selectedOptions.length > 0) {
      return variant.selectedOptions.map(opt => opt.value).join(' / ');
    }

    // Fallback to option values
    const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
    return options.length > 0 ? options.join(' / ') : 'Bundle';
  }

  /**
   * Toggle comparison table visibility
   */
  toggleCompareTable() {
    const tableContainer = this.querySelector('[data-bundle-compare-table]');
    const compareBtn = this.querySelector('[data-bundle-compare-btn]');
    const bundleGrid = this.querySelector('[data-variant-bundle-grid]');

    if (!tableContainer || !compareBtn) return;

    const isExpanded = compareBtn.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      // Hide table, show grid
      tableContainer.style.display = 'none';
      if (bundleGrid) bundleGrid.style.display = '';
      compareBtn.setAttribute('aria-expanded', 'false');
      compareBtn.textContent = this.getCompareButtonText();
    } else {
      // Show table, hide grid
      this.renderComparisonTable();
      tableContainer.style.display = 'block';
      if (bundleGrid) bundleGrid.style.display = 'none';
      compareBtn.setAttribute('aria-expanded', 'true');
      compareBtn.textContent = this.getHideCompareButtonText();
    }
  }

  /**
   * Get compare button text
   * @returns {string}
   */
  getCompareButtonText() {
    return this.dataset.compareText || 'Vergleichen';
  }

  /**
   * Get hide compare button text
   * @returns {string}
   */
  getHideCompareButtonText() {
    return this.dataset.hideCompareText || 'Ausblenden';
  }

  /**
   * Update compare button visibility and table
   */
  updateCompareUI() {
    const compareBtn = this.querySelector('[data-bundle-compare-btn]');
    const tableContainer = this.querySelector('[data-bundle-compare-table]');
    const bundleGrid = this.querySelector('[data-variant-bundle-grid]');

    if (!compareBtn) return;

    const shouldShow = this.shouldShowCompareButton();
    compareBtn.style.display = shouldShow ? '' : 'none';

    // If button was hidden while table was visible, hide table and show grid
    if (!shouldShow && tableContainer) {
      tableContainer.style.display = 'none';
      if (bundleGrid) bundleGrid.style.display = '';
      compareBtn.setAttribute('aria-expanded', 'false');
      compareBtn.textContent = this.getCompareButtonText();
    }

    // If table is visible, refresh it with new selected variant highlighting
    if (tableContainer && tableContainer.style.display !== 'none' && tableContainer.style.display !== '') {
      this.renderComparisonTable();
    }
  }

  /**
   * Handle add to cart button click
   * @param {Event} event - The click event
   */
  async handleAddToCart(event) {
    event.preventDefault();

    if (!this.selectedVariant || !this.selectedVariant.available) {
      console.warn('Cannot add to cart: no variant selected or variant unavailable');
      return;
    }

    const addToCartButton = event.target;

    try {
      // Add to cart using Shopify's cart API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id: this.selectedVariant.id,
          quantity: 1,
        }),
      });

      if (response.ok) {
        const cartData = await response.json();
        console.log('Successfully added to cart:', cartData);
        
        // Get the current cart count
        const cartResponse = await fetch('/cart.js');
        const cartInfo = await cartResponse.json();
        
        // Trigger fly-to-cart animation
        this.triggerFlyToCartAnimation(addToCartButton);
        
        // Update cart icon count
        this.updateCartIconCount(cartInfo.item_count);
        
        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Close the dialog after a brief delay to allow animation to start
        setTimeout(() => {
          this.closeDialog();
        }, 100);

        // Show success message (optional)
        this.showSuccessMessage();
      } else {
        const errorData = await response.json();
        console.error('Cart add failed with response:', errorData);
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage('Failed to add product to cart');
    }
  }

  /**
   * Show success message after adding to cart
   */
  showSuccessMessage() {
    console.log('Product added to cart successfully');
  }

  /**
   * Show error message
   * @param {string} message - The error message
   */
  showErrorMessage(message) {
    // You can customize this to show error notifications
    console.error(message);
  }

  /**
   * Close dialog and reset state (public method)
   */
  close() {
    this.closeDialog();
    
    // Reset state
    this.currentProduct = null;
    this.selectedOptions.clear();
    this.selectedVariant = null;
  }

  /**
   * Trigger fly-to-cart animation
   * @param {HTMLElement} button - The add to cart button
   */
  triggerFlyToCartAnimation(button) {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const productImage = this.querySelector('[data-variant-image]');
    
    if (!cartIcon || !button) return;
    
    // Create fly-to-cart element if the theme supports it
    if (window.customElements && window.customElements.get('fly-to-cart')) {
      const flyToCartElement = document.createElement('fly-to-cart');
      
      if (productImage) {
        const imageUrl = productImage.src || productImage.dataset.src;
        if (imageUrl) {
          flyToCartElement.style.setProperty('background-image', `url(${imageUrl})`);
        }
      }
      
      flyToCartElement.source = button;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
    } else {
      // Fallback animation
      const rect = button.getBoundingClientRect();
      const cartRect = cartIcon.getBoundingClientRect();
      
      const flyingItem = document.createElement('div');
      flyingItem.style.cssText = `
        position: fixed;
        z-index: 9999;
        width: 30px;
        height: 30px;
        background: var(--color-accent, #000);
        border-radius: 50%;
        pointer-events: none;
        transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        left: ${rect.left + rect.width / 2 - 15}px;
        top: ${rect.top + rect.height / 2 - 15}px;
      `;
      
      if (productImage && productImage.src) {
        flyingItem.style.backgroundImage = `url(${productImage.src})`;
        flyingItem.style.backgroundSize = 'cover';
        flyingItem.style.backgroundPosition = 'center';
      }
      
      document.body.appendChild(flyingItem);
      
      // Trigger animation
      requestAnimationFrame(() => {
        flyingItem.style.left = `${cartRect.left + cartRect.width / 2 - 15}px`;
        flyingItem.style.top = `${cartRect.top + cartRect.height / 2 - 15}px`;
        flyingItem.style.transform = 'scale(0.1)';
        flyingItem.style.opacity = '0.3';
      });
      
      // Clean up
      setTimeout(() => {
        if (flyingItem.parentNode) {
          flyingItem.parentNode.removeChild(flyingItem);
        }
      }, 800);
    }
  }

  /**
   * Update cart icon count
   * @param {number} itemCount - The cart item count
   */
  updateCartIconCount(itemCount) {
    const cartCountElements = document.querySelectorAll('.cart-bubble__text-count, .cart-count-bubble, [data-cart-count]');
    
    cartCountElements.forEach(element => {
      if (element) {
        element.textContent = itemCount;
        
        const bubble = element.closest('.cart-bubble');
        if (bubble) {
          bubble.classList.toggle('visually-hidden', itemCount === 0);
        }
      }
    });
    
    const cartIcons = document.querySelectorAll('.header-actions__cart-icon');
    cartIcons.forEach(icon => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
    });
    
    // Store in session
    sessionStorage.setItem('cart-count', JSON.stringify({
      value: String(itemCount),
      timestamp: Date.now()
    }));
  }
}

// Register the custom element
if (!customElements.get('variant-selection-dialog')) {
  customElements.define('variant-selection-dialog', VariantSelectionDialog);
}